import { centroid as turfCentroid } from '@turf/turf';
import type { Geometry } from 'geojson';
import { z } from 'zod';
import type {
  OrgEventChecklistItemDef,
  OrgEventNotifyConfig,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import type { CreateNotificationFormFields } from 'hooks/useNotification';
import type {
  CreateOrgEventPayload,
  OrgEvent,
  UpdateOrgEventPayload,
} from 'hooks/orgEvents/orgEventHooks';
import { NotificationType } from 'hooks/useNotification';
import { categoryFieldsFromSaved, resolvedCategory } from './eventCategory';
import type { RuleStepState } from './ruleStepModel';
import { channelLabels } from './ruleStepModel';

export const CREATE_EVENT_SEVERITY_OPTIONS = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
] as const;

export const CREATE_EVENT_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Monitoring', label: 'Monitoring' },
  { value: 'Demobilizing', label: 'Demobilizing' },
] as const;

export type CreateEventFormValues = {
  name: string;
  type: string;
  customCategory: string;
  severity: string;
  status: string;
  aorIds: string[];
  thresholdDescription: string;
  startedAt: string;
  lat: string;
  lon: string;
  geometry: Geometry | null;
  notificationTypes: string[];
  userIds: string[];
  teamIds: string[];
  priority: string;
  confirmReceipt: boolean;
  checklistItems: OrgEventChecklistItemDef[];
  impactedResourceIds: string[];
  perAssetChecklists: Record<string, OrgEventChecklistItemDef[]>;
};

export const createEventFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(250),
  type: z.string(),
  customCategory: z.string(),
  severity: z.string(),
  status: z.string(),
  aorIds: z.array(z.string()),
  thresholdDescription: z.string(),
  startedAt: z.string(),
  lat: z.string(),
  lon: z.string(),
  geometry: z.custom<Geometry | null>(),
  notificationTypes: z.array(z.string()),
  userIds: z.array(z.string()),
  teamIds: z.array(z.string()),
  priority: z.string(),
  confirmReceipt: z.boolean(),
  checklistItems: z.array(z.custom<OrgEventChecklistItemDef>()),
  impactedResourceIds: z.array(z.string()),
  perAssetChecklists: z.record(
    z.string(),
    z.array(z.custom<OrgEventChecklistItemDef>())
  ),
});

export const CREATE_EVENT_FORM_DEFAULTS: CreateEventFormValues = {
  name: '',
  type: 'Tropical Storm',
  customCategory: '',
  severity: 'Medium',
  status: 'Active',
  aorIds: [],
  thresholdDescription: '',
  startedAt: '',
  lat: '',
  lon: '',
  geometry: null,
  notificationTypes: [NotificationType.IN_APP],
  userIds: [],
  teamIds: [],
  priority: 'watch',
  confirmReceipt: false,
  checklistItems: [],
  impactedResourceIds: [],
  perAssetChecklists: {},
};

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function latLonFromGeometry(geometry: Geometry | null | undefined): {
  lat: string;
  lon: string;
} {
  if (!geometry) return { lat: '', lon: '' };
  if (geometry.type === 'Point') {
    const [lon, lat] = geometry.coordinates;
    if (typeof lon !== 'number' || typeof lat !== 'number') {
      return { lat: '', lon: '' };
    }
    return { lat: String(lat), lon: String(lon) };
  }
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const [lon, lat] = turfCentroid(geometry).geometry.coordinates;
    if (typeof lon !== 'number' || typeof lat !== 'number') {
      return { lat: '', lon: '' };
    }
    return { lat: String(lat), lon: String(lon) };
  }
  return { lat: '', lon: '' };
}

function geometryFromLatLon(
  lat: number | null | undefined,
  lon: number | null | undefined
): Geometry | null {
  if (
    lat == null ||
    lon == null ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    return null;
  }
  return { type: 'Point', coordinates: [lon, lat] };
}

function parseOptionalDateTime(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function regionFromFixedAors(
  aorIds: string[],
  aorNameById: Map<string, string>
): string | null {
  const names = aorIds
    .map((id) => aorNameById.get(id))
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : null;
}

export function eventNotifyConfigFromNotification(
  fields: CreateNotificationFormFields
): OrgEventNotifyConfig | undefined {
  const audience =
    (fields.userIds?.length ?? 0) +
    (fields.teamIds?.length ?? 0) +
    (fields.audienceAorItemIds?.length ?? 0) +
    (fields.audienceGeometry ? 1 : 0);
  if ((fields.notificationTypes?.length ?? 0) === 0 || audience === 0) {
    return undefined;
  }
  return {
    notificationTypes: fields.notificationTypes,
    userIds: fields.userIds ?? [],
    teamIds: fields.teamIds ?? [],
    priority: fields.priority,
    confirmReceipt: Boolean(fields.acknowledgementRequired),
    titleTemplate: 'Event: {eventName}',
    bodyTemplate: '{eventName}',
    emailSubject: fields.emailSubject,
    emailMessage: fields.emailMessage,
    smsMessage: fields.smsMessage,
    teamsMessage: fields.teamsMessage,
    phoneMessage: fields.phoneMessage,
    appMessage: fields.appMessage,
    scheduledDate: fields.scheduledDate
      ? fields.scheduledDate.toISOString()
      : null,
    audienceGeometry: fields.audienceGeometry ?? null,
    audienceAorItemIds: fields.audienceAorItemIds ?? [],
    audienceLocationMatch: fields.audienceLocationMatch ?? null,
    responseOptions: fields.responseOptions ?? null,
    escalationPlan: fields.escalationPlan ?? null,
    incidentId: fields.incidentId,
    aors: fields.aors ?? [],
  };
}

export function buildManualNotifyConfig(
  values: Pick<
    CreateEventFormValues,
    'notificationTypes' | 'userIds' | 'teamIds' | 'priority' | 'confirmReceipt'
  >
): OrgEventNotifyConfig | undefined {
  const channels = values.notificationTypes ?? [];
  const recipients =
    (values.userIds?.length ?? 0) + (values.teamIds?.length ?? 0);
  if (channels.length === 0 || recipients === 0) return undefined;
  return {
    notificationTypes: channels,
    userIds: values.userIds ?? [],
    teamIds: values.teamIds ?? [],
    priority: values.priority || 'watch',
    confirmReceipt: Boolean(values.confirmReceipt),
    titleTemplate: 'Event: {eventName}',
    bodyTemplate: '{eventName}',
  };
}

export function aorIdsFromRegion(
  region: string | null | undefined,
  aorIdByName: Map<string, string>
): string[] {
  if (!region?.trim()) return [];
  const ids: string[] = [];
  for (const part of region.split(',')) {
    const name = part.trim();
    if (!name) continue;
    const id = aorIdByName.get(name) ?? aorIdByName.get(name.toLowerCase());
    if (id) ids.push(id);
  }
  return ids;
}

export function toDatetimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function eventToFormValues(
  event: OrgEvent,
  aorIdByName: Map<string, string>
): CreateEventFormValues {
  const aorIds = aorIdsFromRegion(event.region, aorIdByName);
  const geometry = event.geometry ?? geometryFromLatLon(event.lat, event.lon);
  const derived = latLonFromGeometry(geometry);
  const category = categoryFieldsFromSaved(
    event.type,
    CREATE_EVENT_FORM_DEFAULTS.type
  );
  return {
    ...CREATE_EVENT_FORM_DEFAULTS,
    name: event.name ?? '',
    type: category.eventType,
    customCategory: category.customCategory,
    severity: event.severity || 'Medium',
    status: event.status || 'Active',
    aorIds,
    thresholdDescription: event.threshold_description ?? '',
    startedAt: toDatetimeLocal(event.started_at),
    lat: derived.lat || (event.lat != null ? String(event.lat) : ''),
    lon: derived.lon || (event.lon != null ? String(event.lon) : ''),
    geometry,
    impactedResourceIds: [...(event.impacted_resource_ids ?? [])],
  };
}

export function formToCreateEventPayload(
  values: CreateEventFormValues,
  context: {
    groupGuid: string;
    userGuid: string | null;
    aorNameById: Map<string, string>;
    resourceNameById: Map<string, string>;
  }
): CreateOrgEventPayload {
  const region = regionFromFixedAors(values.aorIds, context.aorNameById);
  const notify = buildManualNotifyConfig(values);
  const checklistItems = values.checklistItems ?? [];
  const impactedResourceIds = [...new Set(values.impactedResourceIds ?? [])];
  const perAssetChecklists = impactedResourceIds
    .map((resourceSetId) => ({
      resourceSetId,
      name: context.resourceNameById.get(resourceSetId),
      items: values.perAssetChecklists?.[resourceSetId] ?? [],
    }))
    .filter((entry) => entry.items.length > 0);
  const derived = latLonFromGeometry(values.geometry);

  return {
    group_guid: context.groupGuid,
    name: values.name.trim(),
    type: emptyToNull(resolvedCategory(values.type, values.customCategory)),
    severity: values.severity,
    status: values.status,
    region,
    lon: parseOptionalNumber(derived.lon || values.lon),
    lat: parseOptionalNumber(derived.lat || values.lat),
    geometry: values.geometry,
    started_at: parseOptionalDateTime(values.startedAt),
    threshold_description: emptyToNull(values.thresholdDescription),
    creation_kind: 'user',
    created_by_user_guid: context.userGuid,
    impacted_resource_ids: impactedResourceIds,
    notify,
    checklist_items: checklistItems.length > 0 ? checklistItems : undefined,
    per_asset_checklists:
      perAssetChecklists.length > 0 ? perAssetChecklists : undefined,
  };
}

export function formToUpdateEventPayload(
  values: CreateEventFormValues,
  context: {
    aorNameById: Map<string, string>;
    resourceNameById: Map<string, string>;
  }
): UpdateOrgEventPayload {
  const created = formToCreateEventPayload(values, {
    groupGuid: '',
    userGuid: null,
    aorNameById: context.aorNameById,
    resourceNameById: context.resourceNameById,
  });
  const {
    group_guid: _group,
    creation_kind: _kind,
    created_by_user_guid: _by,
    ...rest
  } = created;
  return rest;
}

export type CreateEventStepId =
  | 'event'
  | 'notify'
  | 'checklist'
  | 'assetChecklists';

export function createEventDetailsStep(
  values: CreateEventFormValues
): RuleStepState {
  const category = resolvedCategory(values.type, values.customCategory);
  const parts = [category, values.severity, values.status].filter(Boolean);
  return {
    status: values.name.trim() ? 'complete' : 'incomplete',
    summary: values.name.trim()
      ? `${values.name.trim()} · ${parts.join(' · ')}`
      : parts.length > 0
        ? parts.join(' · ')
        : 'Not configured',
    errors: values.name.trim() ? [] : ['Name is required.'],
    warnings: [],
  };
}

export function createEventNotifyStep(
  values: CreateEventFormValues,
  options?: { additive?: boolean }
): RuleStepState {
  const channels = channelLabels(values.notificationTypes ?? []);
  const recipients =
    (values.userIds?.length ?? 0) + (values.teamIds?.length ?? 0);
  if (options?.additive) {
    const ready = channels.length > 0 && recipients > 0;
    return {
      status: ready ? 'complete' : 'incomplete',
      summary: ready
        ? `Sends on save · ${channels.join(', ')} · ${recipients} recipient${
            recipients === 1 ? '' : 's'
          }`
        : 'Not configured',
      errors: [],
      warnings: [],
    };
  }
  const warnings: string[] = [];
  if (channels.length === 0) warnings.push('No channels selected.');
  if (recipients === 0) warnings.push('No recipients selected.');
  const summary =
    channels.length === 0 && recipients === 0
      ? 'Not configured'
      : `${channels.join(', ') || 'No channels'} · ${recipients} recipient${
          recipients === 1 ? '' : 's'
        }`;
  return {
    status: warnings.length > 0 ? 'incomplete' : 'complete',
    summary,
    errors: [],
    warnings,
  };
}

export function createEventChecklistStep(
  values: CreateEventFormValues,
  options?: { additive?: boolean }
): RuleStepState {
  const count = values.checklistItems?.length ?? 0;
  if (options?.additive) {
    return {
      status: count === 0 ? 'incomplete' : 'complete',
      summary:
        count === 0
          ? 'No tasks to add'
          : `adds ${count} task${count === 1 ? '' : 's'}`,
      errors: [],
      warnings: [],
    };
  }
  return {
    status: count === 0 ? 'incomplete' : 'complete',
    summary:
      count === 0 ? 'No tasks yet' : `${count} task${count === 1 ? '' : 's'}`,
    errors: [],
    warnings: count === 0 ? ['No tasks added yet.'] : [],
  };
}

export function createEventAssetStep(
  values: CreateEventFormValues
): RuleStepState {
  const count = values.impactedResourceIds?.length ?? 0;
  return {
    status: count === 0 ? 'incomplete' : 'complete',
    summary:
      count === 0 ? 'No assets yet' : `${count} asset${count === 1 ? '' : 's'}`,
    errors: [],
    warnings: count === 0 ? ['No assets assigned yet.'] : [],
  };
}
